import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppDrawer } from './ui/AppDrawer';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import { Loader2, Trash2, Package, Save, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { predictiveRestockAPI } from '../lib/v2Api';

/**
 * Stage 11C — Van Restock Review Drawer
 *
 * Fetches predicted restock rows for a given van, lets the user review/edit/remove,
 * then saves the list via POST /api/inventory/replenishment-plan/save.
 */
export function VanRestockReviewDrawer({ open, onOpenChange, vanId, vanName }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchPredictions = useCallback(async () => {
    if (!vanId) return;
    setLoading(true);
    try {
      const res = await predictiveRestockAPI.getPlan({
        mode: 'hybrid',
        source_type: 'vehicle',
        source_id: vanId,
      });
      // Only keep rows that have a positive restock suggestion
      const needsRestock = (res.data || []).filter(r => r.suggested_restock_qty > 0);
      setRows(needsRestock.map(r => ({ ...r, editQty: r.suggested_restock_qty })));
    } catch {
      toast.error('Failed to generate restock plan');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [vanId]);

  useEffect(() => {
    if (open && vanId) fetchPredictions();
    if (!open) setRows([]);
  }, [open, vanId, fetchPredictions]);

  const removeRow = (itemId) => {
    setRows(prev => prev.filter(r => r.item_id !== itemId));
  };

  const updateQty = (itemId, value) => {
    setRows(prev => prev.map(r =>
      r.item_id === itemId ? { ...r, editQty: Math.max(0, parseInt(value) || 0) } : r
    ));
  };

  const handleSave = async () => {
    const validRows = rows.filter(r => r.editQty > 0);
    if (validRows.length === 0) {
      toast.error('No items to save');
      return;
    }
    setSaving(true);
    try {
      const res = await predictiveRestockAPI.savePlan({
        name: `Auto Restock — ${vanName}`,
        items: validRows.map(r => ({
          item_id: r.item_id,
          source_type: r.source_type,
          source_id: r.source_id,
          suggested_qty: r.editQty,
        })),
      });
      toast.success(`Restock list created with ${res.data.total_items} items`);
      onOpenChange(false);
      navigate('/replenishment');
    } catch {
      toast.error('Failed to save restock list');
    } finally {
      setSaving(false);
    }
  };

  const totalQty = rows.reduce((sum, r) => sum + (r.editQty || 0), 0);
  const totalCost = rows.reduce((sum, r) => {
    const uc = r.unit_cost || 0;
    return sum + uc * (r.editQty || 0);
  }, 0);

  return (
    <AppDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={`Restock Plan — ${vanName || 'Vehicle'}`}
      description="Review and adjust quantities before saving."
      className="sm:max-w-2xl"
      data-testid="van-restock-review-drawer"
      footer={
        <>
          <div className="flex-1 text-xs text-muted-foreground">
            {rows.length} item{rows.length !== 1 ? 's' : ''} &middot; {totalQty} units
            {totalCost > 0 && ` · $${totalCost.toFixed(2)} est.`}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || rows.length === 0} data-testid="save-restock-plan-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save Restock List
          </Button>
        </>
      }
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">Generating restock plan...</p>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Package className="w-10 h-10 text-muted-foreground/40 mb-3" />
          <h3 className="font-medium text-sm mb-1">No restock needed</h3>
          <p className="text-xs text-muted-foreground">All items on this vehicle are within healthy levels.</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <Table data-testid="restock-review-table">
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead>Item</TableHead>
                <TableHead className="text-right w-[70px]">On Hand</TableHead>
                <TableHead className="text-right w-[70px]">Target</TableHead>
                <TableHead className="text-right w-[90px]">Restock Qty</TableHead>
                <TableHead className="w-[90px]">Reason</TableHead>
                <TableHead className="w-[40px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.item_id} data-testid={`restock-row-${row.item_id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-sm">{row.item_name}</p>
                      {row.item_sku && <p className="text-[10px] text-muted-foreground">{row.item_sku}</p>}
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">{row.qty_on_hand}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{row.target_qty ?? '—'}</TableCell>
                  <TableCell className="text-right">
                    <Input
                      type="number"
                      min="0"
                      value={row.editQty}
                      onChange={(e) => updateQty(row.item_id, e.target.value)}
                      className="h-7 w-[75px] text-xs font-mono text-right ml-auto"
                      data-testid={`restock-qty-${row.item_id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[9px] font-normal whitespace-nowrap">
                      {row.reason.includes('Velocity') && row.reason.includes('Target') ? 'hybrid' :
                       row.reason.includes('Velocity') ? 'velocity' :
                       row.reason.includes('Target') ? 'target' : 'auto'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeRow(row.item_id)}
                      data-testid={`remove-restock-row-${row.item_id}`}
                      className="h-7 w-7 p-0"
                    >
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </AppDrawer>
  );
}
